'use client';

import { useRef } from 'react';
import { useProgressInViewport } from '../../hooks/scroll/useProgressInViewport';
import { getAssetUrl, getCompressedAssetUrl } from 'lib/config/assetUrls';
import styles from './Device.module.css';

const lights = getCompressedAssetUrl('/lights-metabolic.png');
const deviceSvg = getAssetUrl('/device-metabolic.svg');
const DEVICE_SIZE = 574;

export const Device = ({
  className,
  styles: inlineStyles,
}: {
  className?: string;
  styles?: Record<string, unknown>;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const deviceRef = useRef<HTMLDivElement>(null);
  const colorsRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);

  useProgressInViewport({
    containerRef,
    startAtZero: false,
    callback: (progress) => {
      if (!deviceRef.current || !colorsRef.current || !textRef.current) {
        return;
      }
      const initialTransform = -(window.innerHeight / 2) + DEVICE_SIZE / 8;
      if (progress < 0) {
        deviceRef.current.style.transform = `translateY(${
          initialTransform - initialTransform * (1 + progress * 2)
        }px)`;
        colorsRef.current.style.opacity = '0';
        textRef.current.style.opacity = '0';
        return;
      }
      if (progress < 1 / 3) {
        deviceRef.current.style.transform = `translateY(0)`;
        colorsRef.current.style.opacity = `${Math.min(1, progress * 3)}`;
        textRef.current.style.opacity = '0';
        return;
      }
      const relativeProgress = (progress - 0.33) / (2 / 3);
      deviceRef.current.style.transform = `scale(${
        1 - relativeProgress / 2
      })`;
      deviceRef.current.style.opacity = `${1 - relativeProgress}`;
      colorsRef.current.style.transform = `scale(${
        1 - relativeProgress / 2
      })`;
      colorsRef.current.style.opacity = `${1 - relativeProgress}`;
      textRef.current.style.transform = `translateY(${
        100 * (1 - relativeProgress * 2)
      }px)`;
      textRef.current.style.opacity = `${Math.min(1, relativeProgress * 2)}`;
    },
  });

  return (
    <div
      className={className ? `${styles.root} ${className}` : styles.root}
      style={inlineStyles}
      ref={containerRef}
    >
      <div className={styles.stickyContainer}>
        <div
          className={styles.colors}
          ref={colorsRef}
          style={{ ['--device-lights' as any]: `url(${lights})` }}
        />
        <div
          className={styles.deviceImage}
          ref={deviceRef}
          style={{ ['--device-svg' as any]: `url(${deviceSvg})` }}
        />
        <div className={`${styles.text} u-grid`} ref={textRef}>
          <div>
            Ultrahuman M1, a continuous Metabolic fitness tracker that helps you
            optimise your lifestyle
          </div>
        </div>
      </div>
    </div>
  );
};
